/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Effect, RMA, RMANote } from "../../utils/Interfaces";
import { Box, Typography, Stack, Card, CardContent, CircularProgress, TextField, IconButton, Tooltip } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { deleteRMANote, editRMANote } from "./materialsRMAFunctions";
import { Cancel, Check, DateRange, Delete, Edit, Person } from "@mui/icons-material";
import { selectId } from "../auth/authSlice";
import { delayedAction } from "../../utils/Functions";

export interface RMANoteCardProps {
    rma: RMA;
    rmaNote: RMANote;
    index: number;
    onRMANoteUpdated: Effect;
}

const RMANoteCard = (props: RMANoteCardProps) => {
    const { rma, rmaNote, index, onRMANoteUpdated } = props;

    const dispatch = useAppDispatch();

    const userId = useAppSelector(selectId);

    const [editingRMANote, setEditingRMANote] = useState<boolean>(false);
    const [rmaNoteUpdates, setRMANoteUpdates] = useState<string>(rmaNote.notes);

    const [submitting, setSubmitting] = useState<boolean>(false);

    return (
        <Card key={index} sx={{ mt: 2, borderRadius: 4 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between">
                    {!editingRMANote && (
                        <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                            <Box>
                                <Typography>{rmaNote.notes}</Typography>
                            </Box>
                            {submitting && (
                                <Box sx={{ ml: 7 }}>
                                    <CircularProgress size={12} />
                                </Box>
                            )}
                            {!submitting && (
                                <Stack direction="row" justifyContent="flex-end">
                                    {(rmaNote.author?.id ?? 0) === userId && (
                                        <Box>
                                            <Tooltip title="Edit your notes.">
                                                <IconButton
                                                    disabled={rma.date_closed !== null}
                                                    size="small"
                                                    onClick={() => setEditingRMANote(true)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box sx={{ mt: -0.5 }}>
                                        <Tooltip title="Delete notes.">
                                            <IconButton
                                                disabled={rma.date_closed !== null}
                                                onClick={() => {
                                                    setSubmitting(true);
                                                    deleteRMANote(
                                                        dispatch,
                                                        rmaNote.id,
                                                        () => {
                                                            setEditingRMANote(false);
                                                            setSubmitting(false);
                                                            void delayedAction(500, () => {
                                                                setRMANoteUpdates("");
                                                            });
                                                            onRMANoteUpdated();
                                                        },
                                                        () => {}
                                                    );
                                                }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    )}
                    {editingRMANote && (
                        <Stack direction="row" justifyContent="space-betweeen" sx={{ mt: -3, width: "100%" }}>
                            <TextField
                                disabled={submitting}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mt: 3 }}
                                value={rmaNoteUpdates}
                                onChange={(event) => setRMANoteUpdates(event.target.value)}
                                label="Edit RMA Notes"
                                name="edit_rma_notes"
                            />
                            {submitting && (
                                <Box sx={{ mt: 2, ml: 9.75 }}>
                                    <CircularProgress size={12} />
                                </Box>
                            )}
                            {!submitting && (
                                <Stack direction="row" justifyContent="flex-end" sx={{ ml: 3, mt: 3 }}>
                                    {(rmaNote.author?.id ?? 0) === userId && (
                                        <Box>
                                            <Tooltip title="Cancel Changes.">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingRMANote(false);
                                                        void delayedAction(500, () => {
                                                            setRMANoteUpdates("");
                                                        });
                                                    }}
                                                >
                                                    <Cancel fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box sx={{ mt: -0.5 }}>
                                        <Tooltip title="Submit Changes.">
                                            <IconButton
                                                disabled={rmaNoteUpdates === rmaNote.notes}
                                                onClick={() => {
                                                    setSubmitting(true);
                                                    editRMANote(
                                                        dispatch,
                                                        rmaNote.id,
                                                        rmaNoteUpdates,
                                                        () => {
                                                            setEditingRMANote(false);
                                                            setSubmitting(false);
                                                            void delayedAction(500, () => {
                                                                setRMANoteUpdates("");
                                                            });
                                                            onRMANoteUpdated();
                                                        },
                                                        () => {}
                                                    );
                                                }}
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: -1.5, mt: 2 }}>
                    <Stack direction="row" justifyContent="flex-start">
                        <Person fontSize="small" />
                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                            {rmaNote.author?.first_name ?? "--"}&nbsp;{rmaNote.author?.last_name}
                        </Typography>
                        {rmaNote.edited && <Typography variant="subtitle2">&nbsp;• Edited</Typography>}
                    </Stack>
                    <Box sx={{ mr: 5 }} />
                    <Stack direction="row" justifyContent="flex-start">
                        <DateRange fontSize="small" />
                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                            {new Date(rmaNote.last_updated).toLocaleDateString()}&nbsp;
                            {new Date(rmaNote.last_updated).toLocaleTimeString()}
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default RMANoteCard;
